import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import SidebarToggleButton from "../components/SidebarToggleButton";
import ProjectFolderPicker from "../components/ProjectFolderPicker";
import { api, formatError } from "../lib/api";
import { ArrowLeft, Search, Lock, BookOpen, Users, Target, ChevronRight, ChevronLeft, ChevronDown, Table as TableIcon, Star, Filter, X, ArrowUp, ArrowDown, ArrowUpDown, Download, Pencil, Trash2, ListPlus, CheckSquare, Square, FolderPlus, Loader2, Database, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import { isCapabilityTruthy, columnWidthClass, columnLabel, buildFilterConfig } from "../config/databaseFilters";
import { subscribeDbFilters } from "../lib/dbFilterBus";

export default function DatabaseDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // When the caller arrives from `Projects → Open Database` on a starred row
  // the URL carries `?highlight=<row_id>` — we scroll to and pulse that row
  // so the founder can spot the record they starred at a glance.
  const highlightRowId = searchParams.get("highlight") || "";
  const highlightRef = useRef(null);
  const [pulseHighlight, setPulseHighlight] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin", "contributor"].includes((user?.role || "").toLowerCase());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("overview");
  const [stars, setStars] = useState({});
  const [windows, setWindows] = useState(null);
  const [wQuery, setWQuery] = useState("");
  const [filters, setFilters] = useState({});   // { colName: "selected value" }
  const [wFilters, setWFilters] = useState({}); // buyer windows filters
  const [sort, setSort] = useState({ col: null, dir: 1 }); // 1 = asc, -1 = desc
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 200;
  const [editRow, setEditRow] = useState(null); // row being edited by admin
  const [selection, setSelection] = useState(() => new Set()); // row_ids ticked
  const [saveListOpen, setSaveListOpen] = useState(false);

  useEffect(() => { setPage(1); }, [query, sort, filters, slug]);
  const [expandedRows, setExpandedRows] = useState(() => new Set()); // rows whose full detail panel is open

  const toggleRowExpanded = (rid) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };

  // Track the last slug we actually fetched so React StrictMode's double
  // effect invocation doesn't charge the metered `/databases/{slug}` GET
  // twice in dev / preview. In production StrictMode is off, but the guard
  // is cheap and also protects against parent-triggered rerenders.
  const lastFetchedSlug = useRef(null);

  useEffect(() => {
    if (lastFetchedSlug.current === slug) return;
    lastFetchedSlug.current = slug;
    setData(null); setError(null); setQuery(""); setFilters({}); setSelection(new Set());
    // Deep-link tab selection: `?highlight=<id>` implies Data tab; an
    // explicit `?tab=data` also forces Data (used by group-level links).
    const wantsData = !!searchParams.get("highlight") || searchParams.get("tab") === "data";
    setTab(wantsData ? "data" : "overview");
    api.get(`/databases/${slug}`).then((r) => setData(r.data)).catch((e) => setError(formatError(e)));
    api.get("/stars").then((r) => {
      const map = {};
      r.data.filter((s) => s.database_slug === slug).forEach((s) => { map[s.row_id] = true; });
      setStars(map);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (slug !== "retailers" || tab !== "windows") return;
    api.get(`/retailers/windows`, { params: { q: wQuery, limit: 300 } })
      .then((r) => setWindows(r.data)).catch(() => setWindows({ items: [], total: 0 }));
  }, [tab, slug, wQuery]);

  // Deep-link support: when `?highlight=<row_id>` is present (e.g. from
  // Projects → Starred records → Open Database), scroll to the row, expand
  // it inline, and pulse-flash the row for ~2s so the founder can spot it
  // instantly. Depends on `data` so we don't fire before the table paints.
  useEffect(() => {
    if (!highlightRowId || !data?.rows_data) return;
    setExpandedRows((prev) => {
      const next = new Set(prev); next.add(highlightRowId); return next;
    });
    setPulseHighlight(true);
    const scrollT = setTimeout(() => {
      try {
        highlightRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch { /* older browsers ignore behavior */ }
    }, 120);
    const pulseT = setTimeout(() => setPulseHighlight(false), 2400);
    return () => { clearTimeout(scrollT); clearTimeout(pulseT); };
  }, [highlightRowId, data]);

  const toggleStar = async (rowId) => {
    const willStar = !stars[rowId];
    setStars({ ...stars, [rowId]: willStar });
    try {
      if (willStar) await api.post("/stars", { database_slug: slug, row_id: rowId });
      else await api.delete("/stars", { data: { database_slug: slug, row_id: rowId } });
    } catch { setStars((s) => ({ ...s, [rowId]: !willStar })); toast.error("Could not update star"); }
  };

  const deleteRow = async (rowId) => {
    if (!window.confirm("Delete this row permanently?")) return;
    try {
      await api.delete(`/admin/database-rows/${rowId}`);
      setData((d) => ({ ...d, rows_data: (d.rows_data || []).filter((r) => r.id !== rowId) }));
      toast.success("Row deleted");
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveRow = async (rowId, patch) => {
    try {
      const { data: updated } = await api.put(`/admin/database-rows/${rowId}`, patch);
      setData((d) => ({
        ...d,
        rows_data: (d.rows_data || []).map((r) => (r.id === rowId ? { ...r, ...updated } : r)),
      }));
      toast.success("Row updated");
      setEditRow(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  const exportCsv = () => {
    if (!filtered.length) { toast.error("No rows to export"); return; }
    const csvCols = columns;
    // CSV formula-injection guard (SEC-001). If a cell starts with any of
    // =, +, -, @, 0x09 (tab), 0x0D (CR) a spreadsheet treats it as a
    // formula on open — a malicious row planted by an admin / vendor
    // could run against the viewer. Prefix with a single quote so the
    // cell displays literally; this is the OWASP-recommended neutraliser.
    const neutraliseFormula = (s) =>
      /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
    const escape = (v) => {
      if (v == null) return "";
      let s = neutraliseFormula(String(v));
      if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const header = csvCols.map((c) => escape(c.replace(/_/g, " "))).join(",");
    const body = filtered.map((r) => csvCols.map((c) => escape(r[c])).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `${slug}-${filtered.length}rows-${ts}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length.toLocaleString()} rows`);
  };

  const rows = data?.rows_data || [];
  const columns = useMemo(() => {
    // Prefer the DB doc's stored `columns` (server writes it on every
    // import per the schema contract) so table headers + filter
    // dropdowns immediately reflect the current schema. Legacy DBs
    // that never got a `columns` field fall back to the row-key union
    // capped at 8 for readability.
    if (Array.isArray(data?.columns) && data.columns.length) {
      return data.columns.filter((c) => !["id", "database_slug"].includes(c));
    }
    if (!rows.length) return [];
    const set = new Set();
    rows.slice(0, 500).forEach((r) => Object.keys(r).forEach((k) => {
      if (!["id","database_slug"].includes(k)) set.add(k);
    }));
    return Array.from(set).slice(0, 8);
  }, [data, rows]);

  // ── Per-database filter config (curated) ─────────────────────────
  // Curated filter presets are honoured ONLY when every column they
  // reference still exists in the current schema — otherwise the
  // preset is stale (e.g. after Replace All swapped the header) and we
  // fall through to the auto-derived filters so admins never see
  // dropdowns pointing at columns that no longer exist.
  // Admin-configured filter columns (Admin → Databases → Page filters).
  const [filterColumns, setFilterColumns] = useState(null);
  useEffect(() => { setFilterColumns(Array.isArray(data?.filter_columns) ? data.filter_columns : null); }, [data]);
  useEffect(() => {
    const reload = (changed) => {
      if (changed && changed !== slug) return;
      api.get(`/databases/${slug}/filter-config`)
        .then((r) => setFilterColumns(Array.isArray(r.data.filter_columns) ? r.data.filter_columns : null))
        .catch(() => {});
    };
    const unsub = subscribeDbFilters(reload);
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { unsub(); document.removeEventListener("visibilitychange", onVisible); };
  }, [slug]);
  // Filters come ONLY from the saved per-database config (seeded on the
  // server from what each page showed before). No config → no filters,
  // never auto-picked columns.
  const rawCfg = useMemo(
    () => buildFilterConfig((filterColumns || []).filter((c) => columns.includes(c)), rows),
    [filterColumns, columns, rows],
  );
  const cfg = useMemo(() => {
    if (!rawCfg) return null;
    const colSet = new Set(columns);
    const allPresent = (rawCfg.filters || []).every((f) => colSet.has(f.column));
    return allPresent ? rawCfg : null;
  }, [rawCfg, columns]);

  // Distinct-value cache per column (used by `select` / `autocomplete` types).
  // Values are deduped CASE-INSENSITIVELY so "yes / Yes / YES" collapse
  // into one dropdown option; the display casing is the most-frequent
  // one seen in the data (ties broken by first-seen), which typically
  // yields "Yes" over "YES" or "yes" for boolean-ish columns. Filter
  // matching downstream (see `filtered`) already compares lowercase-
  // to-lowercase, so a single canonical option still matches every
  // capitalisation variant in the underlying rows.
  const distinctPerCol = useMemo(() => {
    const cache = {};
    if (!rows.length) return cache;
    const needed = new Set();
    (cfg?.filters || []).forEach((f) => needed.add(f.column));
    if (!cfg) columns.forEach((c) => needed.add(c));
    for (const col of needed) {
      // key = lowercase; value = { display, count, first }
      const buckets = new Map();
      let order = 0;
      for (const r of rows) {
        const raw = String(r[col] ?? "").trim();
        if (!raw || raw.length > 60) continue;
        const key = raw.toLowerCase();
        const existing = buckets.get(key);
        if (existing) {
          existing.count += 1;
          // Track most-common casing; ties keep the earliest seen.
          const displayCount = existing.byCasing.get(existing.display) || 0;
          const rawCount = (existing.byCasing.get(raw) || 0) + 1;
          existing.byCasing.set(raw, rawCount);
          if (rawCount > displayCount) existing.display = raw;
        } else {
          buckets.set(key, {
            display: raw, count: 1, first: order,
            byCasing: new Map([[raw, 1]]),
          });
        }
        order += 1;
      }
      cache[col] = Array.from(buckets.values())
        .sort((a, b) => a.display.localeCompare(b.display))
        .map((b) => b.display);
    }
    return cache;
  }, [rows, cfg, columns]);

  const filtered = useMemo(() => {
    let out = rows;
    // Config-driven filters
    if (cfg) {
      for (const f of (cfg.filters || [])) {
        const val = filters[f.id];
        if (!val) continue;
        if (f.type === "contains") {
          const needle = String(val).toLowerCase();
          out = out.filter((r) => String(r[f.column] ?? "").toLowerCase().includes(needle));
        } else {
          out = out.filter((r) => String(r[f.column] ?? "").toLowerCase() === String(val).toLowerCase());
        }
      }
      for (const g of (cfg.groups || [])) {
        const picked = filters[g.id];
        if (!Array.isArray(picked) || picked.length === 0) continue;
        out = out.filter((r) => picked.some((col) => isCapabilityTruthy(r[col])));
      }
    } else {
      // Fallback: auto-detected exact match on activeFilters
      const activeAuto = Object.entries(filters).filter(([, v]) => v && !Array.isArray(v));
      if (activeAuto.length > 0) {
        out = out.filter((r) =>
          activeAuto.every(([col, val]) => String(r[col] ?? "").toLowerCase() === String(val).toLowerCase())
        );
      }
    }
    // Full-text search — scans across every column (not just the 8 visible ones)
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        Object.entries(r).some(([k, v]) => {
          if (k === "id" || k === "database_slug") return false;
          return String(v ?? "").toLowerCase().includes(q);
        })
      );
    }
    // Sort — numeric where possible, otherwise case-insensitive string compare
    if (sort.col) {
      out = [...out].sort((a, b) => {
        const av = a[sort.col], bv = b[sort.col];
        const na = Number(av), nb = Number(bv);
        const bothNumeric = !isNaN(na) && !isNaN(nb) && av !== "" && bv !== "" && av != null && bv != null;
        let cmp;
        if (bothNumeric) cmp = na - nb;
        else cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true, sensitivity: "base" });
        return cmp * sort.dir;
      });
    }
    return out;
  }, [rows, columns, query, filters, sort, cfg]);

  // ── Auto-detected filterable columns (fallback path) ─────────────
  // Applied only when no curated config is present or valid. Picks up
  // to 5 columns that make GOOD dropdown filters, using both heuristic
  // signals (column-name blacklist) and content stats (uniqueness
  // ratio, avg value length, coverage). Free-text-y columns
  // (description, website, links, notes, …) are excluded even if they
  // sit within the first 5 columns.
  const FILTER_BAD_NAME = /(description|notes?|bio|summary|website|url|link|links|linkedin|email|phone|address|profile|about|comment|reason|content|body|title|slug|id)$/i;

  const columnStats = useMemo(() => {
    // Compute uniqueness + avg length + coverage on RAW row values so
    // we can reject "unique-per-row" columns even when a handful of
    // rows share values.
    const stats = {};
    if (!rows.length) return stats;
    for (const col of columns) {
      let total = 0, filled = 0, lenSum = 0;
      const seen = new Set();
      for (const r of rows) {
        total += 1;
        const v = String(r[col] ?? "").trim();
        if (!v) continue;
        filled += 1;
        lenSum += v.length;
        seen.add(v.toLowerCase());
      }
      stats[col] = {
        distinct:     seen.size,
        coverage:     total ? filled / total : 0,
        avgLen:       filled ? lenSum / filled : 0,
        uniqueRatio:  filled ? seen.size / filled : 0,
      };
    }
    return stats;
  }, [rows, columns]);

  const filterableColumns = useMemo(() => {
    if (cfg) return [];
    const out = [];
    for (const col of columns) {
      if (FILTER_BAD_NAME.test(col)) continue;              // name blacklist
      const arr = distinctPerCol[col] || [];
      if (arr.length < 2) continue;                          // useless dropdown
      if (arr.length > 60) continue;                         // too long dropdown
      const s = columnStats[col];
      if (!s) continue;
      if (s.coverage < 0.3)     continue;                    // mostly empty
      if (s.avgLen  > 40)       continue;                    // long text
      if (s.uniqueRatio > 0.5)  continue;                    // mostly unique
      out.push({ col, values: arr });
      if (out.length === 5) break;                           // cap at 5
    }
    return out;
  }, [distinctPerCol, columns, cfg, columnStats]);

  const activeFilterCount = Object.values(filters).filter((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return !!v;
  }).length;
  const clearFilters = () => setFilters({});

  // Toggle one option inside a capability group.
  const toggleGroupOption = (groupId, column) => {
    setFilters((f) => {
      const cur = Array.isArray(f[groupId]) ? f[groupId] : [];
      const next = cur.includes(column)
        ? cur.filter((c) => c !== column)
        : [...cur, column];
      return { ...f, [groupId]: next };
    });
  };

  // ── Row selection helpers (used by the "Save as list" action bar) ─
  const toggleRowSelected = (rid) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelection(new Set(filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r, i) => r.id || String(i))));
  };
  const clearSelection = () => setSelection(new Set());

  if (error) {
    const isNotFound = /not found/i.test(error);
    const isLimit = /limit reached|monthly.*search/i.test(error);
    const title = isNotFound ? "Not found" : (isLimit ? "Search limit reached" : "Locked");
    return (
      <AppShell title="Databases">
        <Link to="/databases" className="text-sm font-semibold text-shell-side inline-flex items-center gap-1" data-testid="db-back">
          <ArrowLeft className="w-4 h-4" /> All databases
        </Link>
        <div className="mt-8 surface p-10 text-center max-w-xl">
          <Lock className="w-6 h-6 text-primary mx-auto" />
          <h1 className="font-display text-3xl mt-4" data-testid="db-error-title">{title}</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          {isNotFound ? (
            <Link to="/databases" className="btn-primary mt-6 inline-flex" data-testid="db-back-to-list">
              Back to databases
            </Link>
          ) : (
            <Link to="/account/subscription" className="btn-primary mt-6 inline-flex" data-testid="db-upgrade">Upgrade plan</Link>
          )}
        </div>
      </AppShell>
    );
  }

  if (!data) return <AppShell title="Databases"><div className="text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell title={data.name}>
      <div className="w-full max-w-none">
        <Link to="/databases" className="text-sm font-semibold text-shell-side inline-flex items-center gap-1 mb-4" data-testid="db-back">
          <ArrowLeft className="w-4 h-4" /> All databases
        </Link>

        <PageHero
          eyebrow={`Directory · ${rows.length.toLocaleString()} records`}
          icon={Database}
          title={data.name}
          subtitle={data.description}
          right={<SidebarToggleButton testId="db-sidebar-toggle" />}
          testid="db"
        />

        {/* Plan-builder entitlement banner: rows truncated OR search cap hit */}
        {data?.entitlement_meta?.truncated && (
          <div
            className="mt-4 rounded-lg border border-pink-300/60 bg-pink-50 text-pink-900 px-4 py-3 flex items-start gap-3"
            data-testid="db-truncated-banner"
          >
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm leading-snug">
              <p className="font-semibold">
                Showing {data.entitlement_meta.rows_shown.toLocaleString()} of{" "}
                {data.entitlement_meta.rows_total.toLocaleString()} rows.
              </p>
              <p className="text-pink-800/85">
                Your plan caps this database at{" "}
                {data.entitlement_meta.line_cap.toLocaleString()} rows per search.
                Upgrade to see every listing.
              </p>
            </div>
            <Link
              to="/account/subscription"
              className="btn-primary text-xs shrink-0"
              data-testid="db-truncated-upgrade"
            >
              Upgrade
            </Link>
          </div>
        )}
        {data?.entitlement_meta && data.entitlement_meta.search_cap > 0 && (
          <p
            className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground"
            data-testid="db-search-meter"
          >
            Database searches used this month: {data.entitlement_meta.search_used}/{data.entitlement_meta.search_cap}
          </p>
        )}

        {/* Tabs */}
        <div className="border-b border-shell-border">
          <div className="flex gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="db-detail-tabs">
            {[["overview","Overview",BookOpen], ["data","Data",TableIcon],
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`db-detail-tab-${k}`}
                className={["relative inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors whitespace-nowrap",
                  tab === k ? "text-shell-side font-semibold" : "text-muted-foreground hover:text-foreground"].join(" ")}>
                <Icon className="w-4 h-4" /> {label}
                {tab === k && <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-shell-accent" />}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" ? (
          <div className="mt-8 grid md:grid-cols-[1fr_280px] gap-8">
            <div className="prose max-w-none">
              <section>
                <h2 className="font-display text-2xl">What's inside</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed">{data.overview || data.description}</p>
              </section>
              {data.who_it_is_for && (
                <section className="mt-8">
                  <h2 className="font-display text-2xl flex items-center gap-2"><Users className="w-5 h-5 text-shell-accent" /> Who this is for</h2>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{data.who_it_is_for}</p>
                </section>
              )}
              {data.how_to_use && (
                <section className="mt-8">
                  <h2 className="font-display text-2xl flex items-center gap-2"><Target className="w-5 h-5 text-shell-accent" /> How to use it</h2>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{data.how_to_use}</p>
                </section>
              )}
              <div className="mt-10">
                <button onClick={() => setTab("data")} className="btn-amber" data-testid="db-open-data">
                  Open the data <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <aside className="surface p-5 h-fit">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">At a glance</p>
              <div className="mt-4 flex flex-col gap-3">
                <Stat
                  label="Records"
                  value={(data.entitlement_meta?.rows_total ?? rows.length).toLocaleString()}
                  data-testid="db-stat-records"
                />
                <Stat
                  label="Columns"
                  value={columns.length || "—"}
                  data-testid="db-stat-columns"
                />
              </div>
            </aside>
          </div>
        ) : tab === "data" ? (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="relative w-full max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this database…"
                  data-testid="db-search"
                  className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-shell-accent" />
              </div>
              <div className="flex items-center gap-3">
                <p className="text-sm text-muted-foreground" data-testid="db-count">
                  {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
                </p>
                <ExportCsvButton onClick={exportCsv} disabled={!filtered.length} />
              </div>
            </div>

            {/* Column filters — curated per-DB config or auto-detected.
             *  Not sticky: the table scrolls inside its own box (with a
             *  sticky header), so a sticky filter bar only covered rows.
             *  z-20 keeps filter dropdowns above the table header. */}
            {(cfg || filterableColumns.length > 0) && (
              <div className="mt-3 space-y-2 relative z-20 py-2" data-testid="db-filters">
                {/* Row 1: single-value filters (select / autocomplete / contains) */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    <Filter className="w-3 h-3" /> Filter
                  </span>
                  {cfg
                    ? (cfg.filters || []).map((f) => {
                        const val = filters[f.id] || "";
                        const values = distinctPerCol[f.column] || [];
                        const active = !!val;
                        if (f.type === "select") {
                          if (values.length < 2) return null;
                          return (
                            <select
                              key={f.id}
                              value={val}
                              onChange={(e) => setFilters((prev) => ({ ...prev, [f.id]: e.target.value || undefined }))}
                              data-testid={`db-filter-${f.id}`}
                              className={[
                                "db-filter-select text-xs rounded-full pl-3 pr-8 py-1.5 border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none max-w-full truncate",
                                active ? "border-primary text-primary font-semibold" : "border-shell-border text-foreground/80 hover:border-primary/40",
                              ].join(" ")}
                            >
                              <option value="">All {columnLabel(f.label)} ({values.length})</option>
                              {values.map((v) => (
                                <option key={v} value={v}>{v.length > 40 ? v.slice(0, 40) + "…" : v}</option>
                              ))}
                            </select>
                          );
                        }
                        // autocomplete + contains both render as input+datalist.
                        const listId = `db-list-${f.id}`;
                        return (
                          <span key={f.id} className="relative inline-flex items-center w-full sm:w-auto">
                            <input
                              type="text"
                              value={val}
                              onChange={(e) => setFilters((prev) => ({ ...prev, [f.id]: e.target.value || undefined }))}
                              placeholder={f.type === "contains" ? `${columnLabel(f.label)} contains…` : columnLabel(f.label)}
                              list={listId}
                              data-testid={`db-filter-${f.id}`}
                              className={[
                                "text-xs rounded-full px-3 py-1.5 border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors w-full sm:min-w-[180px] sm:w-auto max-w-full",
                                active ? "border-primary text-primary font-semibold" : "border-shell-border text-foreground/80 hover:border-primary/40",
                              ].join(" ")}
                            />
                            <datalist id={listId}>
                              {values.slice(0, 500).map((v) => <option key={v} value={v} />)}
                            </datalist>
                          </span>
                        );
                      })
                    : filterableColumns.map(({ col, values }) => (
                        <select
                          key={col}
                          value={filters[col] || ""}
                          onChange={(e) => setFilters((prev) => ({ ...prev, [col]: e.target.value || undefined }))}
                          data-testid={`db-filter-${col}`}
                          className={[
                            "db-filter-select text-xs rounded-full pl-3 pr-8 py-1.5 border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none max-w-full truncate",
                            filters[col] ? "border-primary text-primary font-semibold" : "border-shell-border text-foreground/80 hover:border-primary/40",
                          ].join(" ")}
                        >
                          <option value="">All {columnLabel(col)}</option>
                          {values.map((v) => (
                            <option key={v} value={v}>{v.length > 40 ? v.slice(0, 40) + "…" : v}</option>
                          ))}
                        </select>
                      ))}
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} data-testid="db-filter-clear"
                      className="text-xs font-semibold text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear {activeFilterCount}
                    </button>
                  )}
                </div>

                {/* Row 2 onwards: capability groups (multi-option pills) */}
                {cfg?.groups?.map((g) => {
                  const picked = Array.isArray(filters[g.id]) ? filters[g.id] : [];
                  return (
                    <div key={g.id} className="flex items-center gap-2 flex-wrap" data-testid={`db-group-${g.id}`}>
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                        {g.label}
                      </span>
                      {g.options.map((opt) => {
                        const on = picked.includes(opt.column);
                        return (
                          <button
                            key={opt.column}
                            type="button"
                            onClick={() => toggleGroupOption(g.id, opt.column)}
                            data-testid={`db-group-${g.id}-${opt.column}`}
                            className={[
                              "text-xs rounded-full px-3 py-1.5 border transition-colors",
                              on
                                ? "border-primary bg-primary text-white font-semibold shadow-sm"
                                : "border-shell-border bg-white text-foreground/80 hover:border-primary/40 hover:text-primary",
                            ].join(" ")}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            <div
              className="mt-4 rounded-2xl border border-shell-border bg-white admin-scroll-x db-table-height"
              data-testid="db-table-wrap"
            >
              {selection.size > 0 && (
                <div
                  className="sticky top-0 z-20 bg-primary text-white px-4 py-2 flex items-center gap-3 shadow-md"
                  data-testid="db-selection-bar"
                >
                  <CheckSquare className="w-4 h-4" />
                  <span className="text-sm font-semibold" data-testid="db-selection-count">
                    {selection.size} selected
                  </span>
                  <button
                    onClick={() => setSaveListOpen(true)}
                    data-testid="db-save-list-open"
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white text-primary px-3 py-1.5 text-xs font-bold hover:bg-white/90"
                  >
                    <ListPlus className="w-3.5 h-3.5" /> Save as list
                  </button>
                  <button
                    onClick={clearSelection}
                    data-testid="db-selection-clear"
                    className="inline-flex items-center gap-1 rounded-full border border-white/40 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              )}
              {rows.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">No rows yet.</div>
              ) : (
                <table className="w-full text-sm min-w-[880px]">
                  <thead className="bg-shell-side text-white text-left sticky top-0 z-10 shadow-md">
                    <tr>
                      <th className="w-10 px-4 py-3 bg-shell-side">
                        <button
                          type="button"
                          onClick={() => (selection.size > 0 ? clearSelection() : selectAllFiltered())}
                          data-testid="db-select-all"
                          title={selection.size > 0 ? "Clear selection" : "Select all visible"}
                          className="p-0.5 rounded hover:bg-white/10 transition"
                        >
                          {selection.size > 0
                            ? <CheckSquare className="w-4 h-4 text-shell-accent" />
                            : <Square className="w-4 h-4 text-white/60" />}
                        </button>
                      </th>
                      <th className="w-10 px-4 py-3 bg-shell-side" aria-label="Expand"></th>
                      <th className="w-10 px-4 py-3 bg-shell-side" aria-label="Star"></th>
                      {columns.map((c) => {
                        const isSorted = sort.col === c;
                        const SortIcon = isSorted ? (sort.dir === 1 ? ArrowUp : ArrowDown) : ArrowUpDown;
                        const nextSort = () => setSort((s) =>
                          s.col === c ? (s.dir === 1 ? { col: c, dir: -1 } : { col: null, dir: 1 }) : { col: c, dir: 1 }
                        );
                        return (
                          <th key={c} className="px-4 py-3 font-semibold text-xs whitespace-nowrap bg-shell-side">
                            <button onClick={nextSort} data-testid={`db-sort-${c}`}
                              className={["inline-flex items-center gap-1 hover:text-shell-accent transition-colors",
                                isSorted ? "text-shell-accent" : "text-white/90"].join(" ")}>
                              {columnLabel(c)}
                              <SortIcon className={["w-3 h-3", isSorted ? "opacity-100" : "opacity-50"].join(" ")} />
                            </button>
                          </th>
                        );
                      })}
                      {isAdmin && <th className="px-4 py-3 font-semibold text-xs whitespace-nowrap bg-shell-side text-right text-white/90">Admin</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r, i) => {
                      const rid = r.id || String(i);
                      const starred = !!stars[rid];
                      const selected = selection.has(rid);
                      const expanded = expandedRows.has(rid);
                      const isHighlighted = rid === highlightRowId;
                      // Extra keys not shown in the top-N columns — surfaced in the expanded detail panel.
                      const extraKeys = Object.keys(r).filter(
                        (k) => !columns.includes(k) && !["id", "database_slug"].includes(k)
                      );
                      return (
                      <React.Fragment key={rid}>
                      <tr data-testid={`db-row-${rid}`}
                        ref={isHighlighted ? highlightRef : null}
                        className={["border-t border-shell-border hover:bg-muted/40 transition-colors",
                          selected ? "bg-primary/5" : "",
                          expanded ? "bg-primary/[0.03]" : "",
                          isHighlighted && pulseHighlight
                            ? "ring-2 ring-shell-accent bg-shell-accent/10 animate-pulse"
                            : isHighlighted ? "bg-shell-accent/5" : "",
                        ].join(" ")}>
                        <td className="px-4 py-3 align-top">
                          <button onClick={() => toggleRowSelected(rid)} data-testid={`db-select-${rid}`}
                            className="p-0.5 rounded hover:bg-background">
                            {selected
                              ? <CheckSquare className="w-4 h-4 text-primary" />
                              : <Square className="w-4 h-4 text-muted-foreground" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <button onClick={() => toggleRowExpanded(rid)} data-testid={`db-expand-${rid}`}
                            title={expanded ? "Hide full details" : "Show full details"}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-colors">
                            {expanded
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <button onClick={() => toggleStar(rid)} data-testid={`db-star-${rid}`}
                            className="p-1 rounded hover:bg-muted">
                            <Star className={["w-4 h-4", starred ? "fill-shell-accent text-shell-accent" : "text-muted-foreground"].join(" ")} />
                          </button>
                        </td>
                        {columns.map((c) => {
                          const w = columnWidthClass(slug, c);
                          return (
                            <td key={c} className={`px-4 py-3 align-top ${w.td}`}>
                              <div className={`break-words whitespace-normal leading-snug ${w.inner}`}
                                   title={typeof r[c] === "string" ? r[c] : undefined}>
                                {formatCell(r[c])}
                              </div>
                            </td>
                          );
                        })}
                        {isAdmin && (
                          <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1">
                              <button onClick={() => setEditRow(r)} data-testid={`db-edit-${rid}`}
                                title="Edit row"
                                className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => deleteRow(rid)} data-testid={`db-delete-${rid}`}
                                title="Delete row"
                                className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {expanded && (
                        <tr data-testid={`db-row-detail-${rid}`} className="bg-muted/30 border-t border-shell-border">
                          <td></td><td></td><td></td>
                          <td colSpan={columns.length + (isAdmin ? 1 : 0)} className="px-4 py-5">
                            <div className="rounded-xl border border-shell-border bg-white p-5 shadow-inner">
                              <p className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold mb-3">
                                Full record
                              </p>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...columns, ...extraKeys].map((k) => (
                                  <div key={k} className="min-w-0">
                                    <p className="text-[10px] text-muted-foreground font-semibold">
                                      {columnLabel(k)}
                                    </p>
                                    <div className="mt-1 text-sm text-foreground break-words whitespace-pre-wrap">
                                      {formatCellFull(r[k])}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
              )}
            </div>
            {filtered.length > PAGE_SIZE && (() => {
              const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
              const currentPage = Math.min(page, totalPages);
              return (
                <div
                  className="mt-4 mb-24 flex items-center justify-between gap-4 rounded-xl border border-shell-border bg-white/95 px-4 py-3 shadow-sm text-xs text-muted-foreground"
                  data-testid="db-detail-pagination"
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="db-detail-page-prev"
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-5 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-primary disabled:hover:border-primary/40"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span data-testid="db-detail-page-label" className="tabular-nums text-center text-xs sm:text-sm">
                    Page <b className="text-foreground">{currentPage}</b> of{" "}
                    <b className="text-foreground">{totalPages}</b> · showing{" "}
                    {(currentPage - 1) * PAGE_SIZE + 1}
                    –{Math.min(currentPage * PAGE_SIZE, filtered.length)} of{" "}
                    {filtered.length.toLocaleString()}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="db-detail-page-next"
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:brightness-110 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="relative w-full max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                <input value={wQuery} onChange={(e) => setWQuery(e.target.value)} placeholder="Search by retailer, banner, or category…"
                  data-testid="windows-search"
                  className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-shell-accent" />
              </div>
              {windows && (
                <p className="text-sm text-muted-foreground" data-testid="windows-count">
                  {(windows.items || []).length.toLocaleString()} of {windows.total.toLocaleString()}
                </p>
              )}
            </div>

            {/* Buyer window filters — retailer / category */}
            {windows && windows.items && windows.items.length > 0 && (() => {
              const buckets = ["retailer", "retailer_s_category", "banner"];
              const filterables = buckets
                .map((col) => {
                  const set = new Set(windows.items.map((w) => String(w[col] || "").trim()).filter(Boolean));
                  if (set.size < 2 || set.size > 40) return null;
                  return { col, values: Array.from(set).sort((a, b) => a.localeCompare(b)) };
                })
                .filter(Boolean);
              if (filterables.length === 0) return null;
              const activeW = Object.values(wFilters).filter(Boolean).length;
              return (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="windows-filters">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    <Filter className="w-3 h-3" /> Filter
                  </span>
                  {filterables.map(({ col, values }) => (
                    <select
                      key={col}
                      value={wFilters[col] || ""}
                      onChange={(e) => setWFilters((f) => ({ ...f, [col]: e.target.value || undefined }))}
                      data-testid={`windows-filter-${col}`}
                      className={[
                        "db-filter-select text-xs rounded-full pl-3 pr-8 py-1.5 border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none",
                        wFilters[col] ? "border-primary text-primary font-semibold" : "border-shell-border text-foreground/80 hover:border-primary/40",
                      ].join(" ")}
                    >
                      <option value="">All {columnLabel(col.replace("retailer_s_", "").replace("retailer s ", ""))}</option>
                      {values.map((v) => (
                        <option key={v} value={v}>{v.length > 40 ? v.slice(0, 40) + "…" : v}</option>
                      ))}
                    </select>
                  ))}
                  {activeW > 0 && (
                    <button onClick={() => setWFilters({})} data-testid="windows-filter-clear"
                      className="text-xs font-semibold text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear {activeW}
                    </button>
                  )}
                </div>
              );
            })()}

            <div
              className="mt-4 rounded-2xl border border-shell-border bg-white admin-scroll-x db-table-height"
              data-testid="windows-table-wrap"
            >
              {!windows ? (
                <div className="p-10 text-center text-muted-foreground">Loading…</div>
              ) : (() => {
                const filteredWindows = (windows.items || []).filter((w) =>
                  Object.entries(wFilters).every(([col, val]) => !val || String(w[col] || "") === val)
                );
                if (filteredWindows.length === 0) {
                  return <div className="p-10 text-center text-muted-foreground">No buyer windows match.</div>;
                }
                return (
                  <table className="w-full text-sm min-w-[880px]">
                    <thead className="bg-shell-side text-white text-left sticky top-0 z-10 shadow-md">
                      <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-xs [&>th]:whitespace-nowrap [&>th]:bg-shell-side [&>th]:text-white/90">
                        <th>Retailer</th><th>Banner</th><th>Category</th>
                        <th>Review Start</th><th>Reset</th><th>NIQ Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWindows.map((w) => (
                        <tr key={w.id} data-testid={`window-row-${w.id}`} className="border-t border-shell-border hover:bg-muted/40">
                          <td className="px-4 py-3 font-semibold">{w.retailer || "—"}</td>
                          <td className="px-4 py-3">{w.banner || "—"}</td>
                          <td className="px-4 py-3">{w.retailer_s_category || "—"}</td>
                          <td className="px-4 py-3">{w.review_start_date || "—"}</td>
                          <td className="px-4 py-3">{w.reset_date || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{w.niq_category_mapping || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {editRow && (
        <RowEditModal row={editRow} columns={columns}
          onClose={() => setEditRow(null)}
          onSave={(patch) => saveRow(editRow.id, patch)} />
      )}
      {saveListOpen && (
        <SaveListModal
          databaseSlug={slug}
          databaseName={data.name}
          rowCount={selection.size}
          rowIds={Array.from(selection)}
          onClose={() => setSaveListOpen(false)}
          onSaved={() => {
            setSaveListOpen(false);
            clearSelection();
            toast.success("List saved — find it in Projects");
          }}
        />
      )}
    </AppShell>
  );
}

function RowEditModal({ row, columns, onClose, onSave }) {
  // Union of the current columns + any additional keys already on the row
  const allKeys = useMemo(() => {
    const set = new Set(columns);
    Object.keys(row).forEach((k) => {
      if (!["id", "database_slug"].includes(k)) set.add(k);
    });
    return Array.from(set);
  }, [row, columns]);
  const [draft, setDraft] = useState(() => {
    const d = {};
    allKeys.forEach((k) => { d[k] = row[k] ?? ""; });
    return d;
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    // Only send fields that actually changed
    const patch = {};
    allKeys.forEach((k) => {
      const before = row[k] ?? "";
      const after = draft[k] ?? "";
      if (String(before) !== String(after)) patch[k] = after === "" ? null : after;
    });
    if (Object.keys(patch).length === 0) { toast.info("Nothing changed"); return; }
    setBusy(true);
    try { await onSave(patch); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4" data-testid="db-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Admin · edit row</p>
            <h3 className="font-display text-2xl mt-1">{row.name || row.brand || "Row"}</h3>
          </div>
          <button onClick={onClose} data-testid="db-edit-close"
            className="p-2 rounded hover:bg-muted text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 grid gap-3">
          {allKeys.map((k) => {
            const val = draft[k] ?? "";
            const long = typeof val === "string" && val.length > 80;
            return (
              <label key={k} className="text-xs text-muted-foreground font-semibold block">
                {columnLabel(k)}
                {long ? (
                  <textarea rows={3} value={val} onChange={(e) => set(k, e.target.value)}
                    data-testid={`db-edit-field-${k}`}
                    className="mt-1 w-full rounded-lg border border-shell-border px-3 py-2 text-sm normal-case tracking-normal text-foreground font-normal" />
                ) : (
                  <input value={val} onChange={(e) => set(k, e.target.value)}
                    data-testid={`db-edit-field-${k}`}
                    className="mt-1 w-full rounded-lg border border-shell-border px-3 py-2 text-sm normal-case tracking-normal text-foreground font-normal" />
                )}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-shell-border">
          <button onClick={onClose} data-testid="db-edit-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="db-edit-save"
            className="btn-primary text-sm disabled:opacity-60">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-2xl">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}

// Export CSV button — lives as its own component so `useMenuVisibility()`
// resolves inside the <AppShell> subtree that owns the real provider.
// When an admin toggles Menu Directory → Databases (all) → Export CSV
// to Visibility Off, the button renders null on every database page.
function ExportCsvButton({ onClick, disabled }) {
  const { isPageElementHidden } = useMenuVisibility();
  if (isPageElementHidden("db-export-csv")) return null;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid="db-export-csv"
      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
    >
      <Download className="w-3.5 h-3.5" /> Export CSV
    </button>
  );
}

function SaveListModal({ databaseSlug, databaseName, rowCount, rowIds, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState(today);
  const [comments, setComments] = useState("");
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give your list a name");
      return;
    }
    setBusy(true);
    try {
      await api.post("/lists", {
        name: name.trim(),
        database_slug: databaseSlug,
        row_ids: rowIds,
        event_date: eventDate || null,
        comments: comments.trim() || null,
        project_id: projectId || null,
      });
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4" data-testid="db-save-list-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1">
            <FolderPlus className="w-3 h-3" /> Save as list
          </p>
          <h3 className="font-display text-2xl mt-1">
            {rowCount} record{rowCount === 1 ? "" : "s"} from <span className="text-primary">{databaseName}</span>
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Lists appear in <Link to="/projects" className="underline hover:text-primary">My Projects</Link> for later reference.
          </p>
        </div>
        <div className="px-6 py-4 grid gap-4">
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kettle Chips · Retail broker shortlist"
              data-testid="save-list-name"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Date</span>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              data-testid="save-list-date"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <ProjectFolderPicker
            value={projectId}
            onChange={setProjectId}
            testidPrefix="save-list-folder"
          />
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Comments (optional)</span>
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Notes about why you picked these, next steps, etc."
              data-testid="save-list-comments"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-none"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="save-list-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="save-list-submit"
            className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
            {busy ? "Saving…" : "Save list"}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatCell(v) {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  const s = String(v);
  if (/^https?:\/\//i.test(s)) {
    return (
      <a href={s} target="_blank" rel="noreferrer"
        className="text-primary hover:underline break-all inline-flex items-center gap-1"
        title={s}>
        {s.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 40)}
        <ExternalLink className="w-3 h-3 opacity-60" />
      </a>
    );
  }
  // Long strings: clamp to 2 lines visually and expose the full value on hover
  // via the native title attribute. Row expansion (chevron) shows the full text.
  if (s.length > 120) {
    return (
      <span className="line-clamp-2 text-foreground/90" title={s}>
        {s}
      </span>
    );
  }
  return <span className="break-words">{s}</span>;
}

function formatCellFull(v) {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  const s = String(v);
  if (/^https?:\/\//i.test(s)) {
    return (
      <a href={s} target="_blank" rel="noreferrer"
        className="text-primary hover:underline break-all inline-flex items-center gap-1">
        {s.replace(/^https?:\/\//, "").replace(/\/$/, "")}
        <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
      </a>
    );
  }
  return <span className="break-words whitespace-pre-wrap">{s}</span>;
}
